import NumericComponent from 'app/components/FormComponents/NumericComponent';
import SelectComponent from 'app/components/FormComponents/SelectComponent';
import * as Yup from 'yup';
import { getPerceptionDeductions } from 'app/service/catalog/Catalogs';
import { SelectOption } from 'app/types/commons';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { PerceptionDeduction } from 'app/types/perceptions';
import {
  getCollaboratorDeductions,
  postDeductionsSecondRoster,
} from 'app/service/deductions/deductions';
import { Collaborator } from 'app/types/collaborator';
import { toaster } from 'app/utilities/commons';

type Props = {
  collaborator?: Collaborator;
  handleCloseModal?: () => void;
  fetchCollaboratorDeductionsId: (id: number | string | undefined) => void;
  fetchCollaboratorSettings?: () => void;
  periodId?: any;
};

type FormValues = {
  typeDeduction: SelectOption | null;
  amount?: number;
};

const initialValues: FormValues = {
  typeDeduction: null,
  amount: 0,
};

const validationSchema = Yup.object({
  typeDeduction: Yup.object({
    value: Yup.number(),
    label: Yup.string(),
    typePerceptionDeductionId: Yup.number(),
  })
    .nullable()
    .required('Este campo es requerido'),
  amount: Yup.number()
        .when('typeDeduction', (typeDeduction, schema) => {
          if (Number(typeDeduction?.typeId) === 3) {
            return schema
              .min(1, 'La cantidad tiene que ser mayor que 0')
              .max(15, 'La cantidad tiene que ser menor que 16')
              .required('Este campo es requerido')
          }
          if (Number(typeDeduction?.typeId) === 4) {
            return schema
              .min(1, 'La cantidad tiene que ser mayor que 0')
              .max(999999, 'La cantidad tiene que ser menor que 1,000,000')
              .required('Este campo es requerido');
          }
          
          
          return schema.notRequired();
        })
});

const DeductionsSettings: React.FC<Props> = ({
  collaborator,
  handleCloseModal,
  fetchCollaboratorDeductionsId,
  fetchCollaboratorSettings,
  periodId
}) => {
  const [show, setShow] = useState(false);
  const [typesDeduction, setTypesDeduction] = useState<PerceptionDeduction[]>(
    []
  );
  const [selectDeduction, setSelectDeduction] = useState<SelectOption[]>([]);
  const [prevRows, setPrevRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const formData = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await postDeductionsSecondRoster({
          collaboratorId: Number(collaborator!.id),
          deductionId: values.typeDeduction?.value,
          value: Number(value),
          periodId: periodId
        });
        formData.resetForm();
        fillDataAsync();
        handleCloseModal?.();
        fetchCollaboratorDeductionsId(collaborator!.id);
        setShow(false);
        fetchCollaboratorSettings?.()
        const message = 'Se actualizó la información de deducciones con éxito';
        toaster.success(message);
      } catch (error: any) {
        console.error('error en submit', error);
      }
    },
  });
  const typeDeduction = useMemo(() => {
    if (formData.values.typeDeduction?.value && typesDeduction) {
      return typesDeduction.find(
        (item) => item.id === formData.values.typeDeduction?.value
      );
    }
    return null;
  }, [formData.values.typeDeduction, typesDeduction]);
  const value = useMemo(() => {
    if (typeDeduction) {
      switch (typeDeduction.typePerceptionDeductionId) {
        case 1:
          return typeDeduction.value;
        case 2:
          switch (typeDeduction.factorBaseId) {
            case 1:
              return (
                typeDeduction.value * Number(collaborator!.employe?.baseSalary)
              );
            case 2:
              return (
                typeDeduction.value * Number(collaborator!.employe?.dayliSalary)
              );
            case 3:
              return (
                (typeDeduction.value *
                  Number(collaborator!.employe?.baseSalary)) /
                collaborator!.employe?.factor[0].value!
              );
            default:
              return (
                typeDeduction.value * Number(collaborator!.employe?.baseSalary)
              );
          }
        case 3:
          return (formData.values.amount ?? 0) * Number(typeDeduction.value);
        case 4:
        default:
          return Number(formData.values.amount) ?? 0;
      }
    }
    return 0;
  }, [typeDeduction, formData.values.amount]);

  const fillDataAsync = async () => {
    try {
      const typesRes = await getPerceptionDeductions();
      const prevRes = await getCollaboratorDeductions(collaborator!.id!).catch(
        (error) => {
          console.error('error al cargar las deducciónes anteriores', error);
        }
      );
      if (prevRes)
        setPrevRows(
          prevRes.map((item) => ({
            deduction: item.deduction,
            value: item.value.toLocaleString(),
          }))
        );
      setTypesDeduction(typesRes.records);
      setSelectDeduction(
        typesRes.records
          .filter((item) => item.typeMovementId === 2)
          .map((item) => ({
            value: item.id,
            label: item.description,
            typeId: item.typePerceptionDeductionId
          }))
      );
    } catch (error) {
      console.error('error en carga de catalogos', error);
    }
  };
  
  useEffect(() => {
    if (collaborator) fillDataAsync();
  }, [collaborator]);
  return (
    <div>
      <div>
        <form
          id="kt_modal_generic_form"
          className="form"
          action="#"
          onSubmit={formData.handleSubmit}
        >
          <div className="m-3">
            <SelectComponent
              options={selectDeduction}
              formData={formData}
              name="typeDeduction"
              display={'-1'}
            />
            {formData.touched.typeDeduction && formData.errors.typeDeduction && (
              <div className="fv-plugins-message-container text-danger w-100">
                <span role="alert">{formData.errors.typeDeduction}</span>
              </div>
            )}
          </div>
          <div className="d-flex flex-column flex-xl-row flex-row-fluid mt-5">
            <div className="w-100 d-flex">
              <div className="w-100 pe-5">
                {typeof typeDeduction?.typePerceptionDeductionId ===
                  'number' && (
                    <div className="container">
                      <div className="row">
                        {[1, 2, 3].includes(
                          typeDeduction?.typePerceptionDeductionId
                        ) && (
                            <div className="col">
                              <h4>Valor</h4>
                              <p>
                                {typeDeduction?.typePerceptionDeductionId === 2
                                  ? `${Math.round(typeDeduction.value * 100)}%`
                                  : typeDeduction.value.toFixed(2)}
                              </p>
                            </div>
                          )}
                        {[3, 4].includes(
                          typeDeduction?.typePerceptionDeductionId
                        ) && (
                            <div className="col">
                              <NumericComponent
                                label="Cantidad"
                                formData={formData}
                                name="amount"
                                max={100}
                              />
                              {formData.errors.amount && formData.touched.amount && (
                                <div className="text-danger">{formData.errors.amount}</div>
                              )}

                            </div>
                          )}
                      </div>
                      <div className="row">
                        <div className="col mt-5">
                          <h4>Valor Total</h4>
                          <p>{typeof value === 'number' ? value.toFixed(2) : '0.00'}</p>
                          {value > 999999 && <div className="text-danger">El valor total no puede ser mayor a 999,999</div>}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-10">
            <button
              type="reset"
              className="btn btn-light me-3"
              data-kt-users-modal-action="cancel"
              onClick={handleCloseModal}
            >
              Cerrar
            </button>
            <button
              type="submit"
              className="btn btn-primary me-3"
              data-kt-users-modal-action="submit"
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeductionsSettings;
